import { useEffect, useState } from 'react';
import {
  getBlogPosts,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  uploadBlogImage,
  getAdminProducts,
  isMissingTable,
} from '../services/adminApi';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import FormModal from '../components/FormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import MissingTable from '../components/MissingTable';

const EMPTY_FORM = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  cover_image: '',
  product_ids: [],
  status: 'draft',
};

/* "Ruby: The Stone of Kings" -> "ruby-the-stone-of-kings" */
const slugify = (text) =>
  String(text)
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(false);
  // Only auto-fill the slug until the author edits it themselves —
  // changing a published post's slug breaks every link to it.
  const [slugTouched, setSlugTouched] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data, error }, { data: prods }] = await Promise.all([
      getBlogPosts(),
      getAdminProducts(),
    ]);
    setPosts(data);
    setProducts(prods || []);
    setLoadError(error);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSlugTouched(false);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      body: post.body || '',
      cover_image: post.cover_image || '',
      product_ids: post.product_ids || [],
      status: post.status || 'draft',
    });
    setSlugTouched(true);
    setFormError('');
    setModalOpen(true);
  };

  const onTitle = (value) => {
    setForm((f) => ({
      ...f,
      title: value,
      slug: slugTouched ? f.slug : slugify(value),
    }));
  };

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id)
        ? f.product_ids.filter((p) => p !== id)
        : [...f.product_ids, id],
    }));
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const { url, error } = await uploadBlogImage(file);
    setUploading(false);
    if (error) {
      setFormError(`Image upload failed: ${error.message}`);
      return;
    }
    set('cover_image', url);
  };

  const save = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.title.trim()) return setFormError('Please give the post a title.');

    const slug = (form.slug.trim() || slugify(form.title)).toLowerCase();
    if (!slug) return setFormError('Please provide a URL slug.');

    const clash = posts.find((p) => p.slug === slug && p.id !== editing?.id);
    if (clash)
      return setFormError(
        `Another post already uses the slug "${slug}". Slugs must be unique.`
      );

    setSaving(true);

    const payload = {
      title: form.title.trim(),
      slug,
      excerpt: form.excerpt.trim() || null,
      body: form.body,
      cover_image: form.cover_image.trim() || null,
      product_ids: form.product_ids,
      status: form.status,
      // Stamp the publish date the first time it goes live, and keep
      // the original date if it's edited later.
      published_at:
        form.status === 'published'
          ? editing?.published_at || new Date().toISOString()
          : null,
    };

    const { error } = editing
      ? await updateBlogPost(editing.id, payload)
      : await createBlogPost(payload);

    setSaving(false);

    if (error) {
      setFormError(`Could not save: ${error.message}`);
      return;
    }

    setModalOpen(false);
    load();
  };

  const confirmDelete = async () => {
    setDeleting(true);
    const { error } = await deleteBlogPost(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (error) alert(`Delete failed: ${error.message}`);
    else load();
  };

  if (!loading && isMissingTable(loadError)) {
    return <MissingTable table="blog_posts" />;
  }

  const published = posts.filter((p) => p.status === 'published').length;

  return (
    <div>
      <div className="page-head">
        <h1>Blog</h1>
        <button className="btn-primary" onClick={openAdd}>
          + Write Post
        </button>
      </div>

      {loadError && !isMissingTable(loadError) && (
        <p className="admin-error">Failed to load posts: {loadError.message}</p>
      )}

      {posts.length > 0 && (
        <p className="admin-hint">
          {published} published · {posts.length - published} draft
          {posts.length - published === 1 ? '' : 's'}. Drafts are invisible on
          the website until you set them to Published.
        </p>
      )}

      <DataTable
        loading={loading}
        emptyMessage="No posts yet. Click “+ Write Post” to publish your first article."
        rows={posts}
        columns={[
          {
            key: 'cover_image',
            label: '',
            width: '64px',
            render: (p) =>
              p.cover_image ? (
                <img src={p.cover_image} alt={p.title} className="thumb" />
              ) : (
                <div className="thumb thumb-empty">📝</div>
              ),
          },
          {
            key: 'title',
            label: 'Post',
            render: (p) => (
              <div>
                <div className="cell-title">{p.title}</div>
                <div className="cell-sub">/blog/{p.slug}</div>
              </div>
            ),
          },
          {
            key: 'product_ids',
            label: 'Products',
            render: (p) => (
              <span className="cell-sub">
                {p.product_ids?.length
                  ? `${p.product_ids.length} linked`
                  : 'none'}
              </span>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            render: (p) => <StatusBadge status={p.status} />,
          },
          {
            key: 'published_at',
            label: 'Published',
            render: (p) => (
              <span className="cell-sub">{fmtDate(p.published_at)}</span>
            ),
          },
          {
            key: 'actions',
            label: 'Actions',
            render: (p) => (
              <div className="action-buttons">
                <button className="btn-edit" onClick={() => openEdit(p)}>
                  Edit
                </button>
                {p.status === 'published' && (
                  <a
                    className="btn-view"
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View
                  </a>
                )}
                <button
                  className="btn-delete"
                  onClick={() => setDeleteTarget(p)}
                >
                  Delete
                </button>
              </div>
            ),
          },
        ]}
      />

      <FormModal
        open={modalOpen}
        wide
        title={editing ? `Edit — ${editing.title}` : 'Write Post'}
        onClose={() => setModalOpen(false)}
      >
        <form onSubmit={save} className="admin-form">
          <label>
            Title *
            <input
              value={form.title}
              onChange={(e) => onTitle(e.target.value)}
              placeholder="Why Rubies Suit a Strong Sun"
            />
          </label>

          <label>
            URL slug *
            <input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                set('slug', slugify(e.target.value));
              }}
              placeholder="why-rubies-suit-a-strong-sun"
            />
            <span className="field-hint">
              The post will live at /blog/{form.slug || 'your-slug'}. Changing
              this on a published post breaks existing links.
            </span>
          </label>

          <label>
            Short summary
            <textarea
              rows={2}
              value={form.excerpt}
              onChange={(e) => set('excerpt', e.target.value)}
              placeholder="One or two lines shown on the blog listing page."
            />
          </label>

          <label>
            Body
            <textarea
              rows={14}
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              placeholder={
                'Write normally. Leave a blank line between paragraphs.\n\n' +
                '## A heading\n\n' +
                '- a bullet point\n- another one\n\n' +
                '**bold text**, and [a link](https://example.com)'
              }
            />
            <span className="field-hint">
              Blank line = new paragraph · <code>## </code> heading ·{' '}
              <code>### </code> smaller heading · <code>- </code> bullet ·{' '}
              <code>&gt; </code> quote · <code>**bold**</code> ·{' '}
              <code>[text](https://link)</code>
            </span>
          </label>

          <label>
            Cover image
            <input
              value={form.cover_image}
              onChange={(e) => set('cover_image', e.target.value)}
              placeholder="https://…"
            />
          </label>
          <label>
            …or upload one
            <input type="file" accept="image/*" onChange={handleImage} />
            {uploading && <span className="field-hint">Uploading…</span>}
          </label>
          {form.cover_image && (
            <img
              src={form.cover_image}
              alt="Cover preview"
              className="image-preview"
            />
          )}

          <label>
            Products featured in this post
            <span className="field-hint">
              These appear at the end of the article with a Buy button.
            </span>
          </label>
          <div className="checkbox-grid">
            {products.length === 0 ? (
              <span className="field-hint">No products to link yet.</span>
            ) : (
              products.map((p) => (
                <label key={p.id} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={form.product_ids.includes(p.id)}
                    onChange={() => toggleProduct(p.id)}
                  />
                  {p.name}
                </label>
              ))
            )}
          </div>

          <label>
            Status
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
            >
              <option value="draft">Draft — only you can see it</option>
              <option value="published">Published — live on the website</option>
            </select>
          </label>

          {formError && <p className="admin-error">{formError}</p>}

          <div className="admin-modal-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || uploading}
            >
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Post'}
            </button>
          </div>
        </form>
      </FormModal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this post?"
        message={`"${deleteTarget?.title}" will be removed permanently.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
